import type { ProviderPricing } from "./model-settings";

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

export function formatPricing(pricing: ProviderPricing): string {
  if (!pricing) return "—";
  const parts: string[] = [];
  if (pricing.input_per_million_usd != null && pricing.output_per_million_usd != null) {
    parts.push(
      `${usd.format(pricing.input_per_million_usd)} in / ${usd.format(pricing.output_per_million_usd)} out per 1M tokens`,
    );
  } else if (pricing.per_million_tokens_usd != null) {
    parts.push(`${usd.format(pricing.per_million_tokens_usd)} per 1M tokens`);
  } else if (pricing.per_thousand_searches_usd != null) {
    parts.push(`${usd.format(pricing.per_thousand_searches_usd)} per 1K searches`);
  }
  return parts.length === 0 ? "—" : parts.join(" · ");
}

function parseDisplayDate(iso: string): Date | null {
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return date;
  } catch {
    return null;
  }
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Pure calendar date `YYYY-MM-DD` (no time / zone). */
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Calendar date for UI: always day/month/year (DD/MM/YYYY).
 * - Pure `YYYY-MM-DD` (and callers that pass `iso.slice(0, 10)`): reorders
 *   digits with no timezone shift — semantic business dates stay on that day.
 * - Full ISO datetimes: viewer's local calendar day.
 */
export function formatDate(iso: string): string {
  const trimmed = iso.trim();
  const dateOnly = DATE_ONLY_RE.exec(trimmed);
  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    return `${day}/${month}/${year}`;
  }
  const date = parseDisplayDate(trimmed);
  if (!date) return iso;
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
}

/**
 * Date + time for UI: DD/MM/YYYY HH:mm (24h) in the viewer's local timezone.
 * Use for absolute timestamps in tables, tooltips, and meta rows.
 */
export function formatTimestamp(iso: string): string {
  const date = parseDisplayDate(iso);
  if (!date) return iso;
  const datePart = `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
  const timePart = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
  return `${datePart} ${timePart}`;
}

/** Alias of {@link formatTimestamp} for call sites that prefer a date+time name. */
export function formatDateTime(iso: string): string {
  return formatTimestamp(iso);
}

/**
 * Elapsed time between `startedAt` and `endedAt` (the run's clock stop; pass
 * `null` for "now", for a task/run still in progress). `"—"` when there is
 * no start time at all; hours once the interval passes one hour.
 */
export function formatDuration(startedAt: string | null, endedAt: string | null): string {
  if (!startedAt) return "—";
  const end = endedAt ? Date.parse(endedAt) : Date.now();
  const seconds = Math.max(0, Math.round((end - Date.parse(startedAt)) / 1_000));
  if (seconds < 60) return `${seconds} s`;
  if (seconds >= 3_600) {
    const hours = Math.floor(seconds / 3_600);
    const minutes = Math.floor((seconds % 3_600) / 60);
    return `${hours} h ${minutes} min`;
  }
  return `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
}

/**
 * Local `<input type="date">` value → UTC ISO instant. The "from" bound is
 * local midnight of that date. Lifted (task 5) from the two previously-
 * identical private copies in `system-tasks.tsx` and `audit.tsx` — no
 * behavior change.
 */
export function localDateToIsoStart(date: string): string | null {
  if (!date) return null;
  const parsed = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

/**
 * The "to" bound is local midnight of the *next* date (exclusive upper
 * bound), so a row created any time on the selected end date is included.
 */
export function localDateToIsoEnd(date: string): string | null {
  if (!date) return null;
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setDate(parsed.getDate() + 1);
  return parsed.toISOString();
}
